import React, { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useNavigation, useNavigationState } from '@react-navigation/native';

import {
	GET_MY_OPEN_SHIFTS,
	GET_MY_ROSTER_SHIFTS,
	GET_MY_UNAVAILABILITIES,
} from '../../../common/graphql/people/roster';
import { GET_MY_TIMESHEETS_PAGE } from '../../../common/graphql/people/timesheet';
import {
	addDaysToRosterDate,
	compareRosterDates,
	formatDurationHours,
	formatRosterDate,
	formatShiftRange,
	getShiftDurationHours,
	getTodayDeviceRosterDate,
	isUpcomingOrPendingUnavailability,
} from '../../../common/utils/people/roster';
import { useUserContext } from '../../../common/utils/context';
import Text from '../../components/text/Text';
import {
	Card,
	EmptyState,
	ListRow,
	Screen,
	SecondaryButton,
} from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import MonthCalendar, { getMonthGridRange, getMonthStart } from './components/MonthCalendar';
import {
	canApplyForOpenShift,
	formatShiftSummary,
	pluralize,
	TIMESHEET_PAGE_SIZE,
	UPCOMING_OPEN_SHIFT_DAYS,
} from './rosterHelpers';

// My roster: month calendar of published shifts and open shifts, the selected day's shifts,
// and links to Open shifts, My timesheets and Leave (self-service, mirroring People My account).
const MyRosterView = () => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	const { isAVC } = useUserContext();
	const previousRouteName = useNavigationState(state => state.routes[state.index - 1]?.name);

	// Device-local day: the driver/picker is physically in their state.
	const today = useMemo(() => getTodayDeviceRosterDate(), []);
	const openShiftsToDate = useMemo(() => addDaysToRosterDate(today, UPCOMING_OPEN_SHIFT_DAYS), [today]);
	const [month, setMonth] = useState(() => getMonthStart(today));
	const [selectedDate, setSelectedDate] = useState(today);
	const gridRange = useMemo(() => getMonthGridRange(month), [month]);

	const {
		data: shiftsData,
		loading: shiftsLoading,
		error: shiftsError,
		refetch: refetchShifts,
	} = useQuery(GET_MY_ROSTER_SHIFTS, {
		skip: !isAVC,
		variables: gridRange,
		fetchPolicy: 'cache-and-network',
	});
	// Open shifts already past cannot be applied for, so the calendar marks only upcoming ones.
	const calendarOpenShiftRange = useMemo(() => (
		compareRosterDates(gridRange.toDate, today) < 0
			? null
			: {
				fromDate: compareRosterDates(gridRange.fromDate, today) < 0 ? today : gridRange.fromDate,
				toDate: gridRange.toDate,
			}
	), [gridRange, today]);
	const {
		data: calendarOpenShiftsData,
		error: calendarOpenShiftsError,
		refetch: refetchCalendarOpenShifts,
	} = useQuery(GET_MY_OPEN_SHIFTS, {
		skip: !isAVC || !calendarOpenShiftRange,
		variables: calendarOpenShiftRange,
		fetchPolicy: 'cache-and-network',
	});
	const {
		data: openShiftsData,
		error: openShiftsError,
	} = useQuery(GET_MY_OPEN_SHIFTS, {
		skip: !isAVC,
		variables: { fromDate: today, toDate: openShiftsToDate },
		fetchPolicy: 'cache-and-network',
	});
	const { data: timesheetData } = useQuery(GET_MY_TIMESHEETS_PAGE, {
		skip: !isAVC,
		variables: { page: 0, perPage: TIMESHEET_PAGE_SIZE },
		fetchPolicy: 'cache-and-network',
	});
	const { data: unavailabilityData } = useQuery(GET_MY_UNAVAILABILITIES, {
		skip: !isAVC,
		fetchPolicy: 'cache-and-network',
	});

	const shifts = useMemo(() => shiftsData?.getMyRosterShifts || [], [shiftsData]);
	const calendarOpenShifts = useMemo(() => (
		calendarOpenShiftRange ? calendarOpenShiftsData?.getMyOpenShifts || [] : []
	), [calendarOpenShiftRange, calendarOpenShiftsData]);
	const markedDates = useMemo(() => {
		const marks = {};
		shifts.forEach((shift) => {
			marks[shift.date] = { ...marks[shift.date], shift: true };
		});
		calendarOpenShifts.forEach((openShift) => {
			marks[openShift.date] = { ...marks[openShift.date], openShift: true };
		});
		return marks;
	}, [calendarOpenShifts, shifts]);
	const selectedShifts = shifts.filter(shift => shift.date === selectedDate);
	const selectedOpenShifts = calendarOpenShifts.filter(openShift => openShift.date === selectedDate);

	const availableOpenShiftCount = (openShiftsData?.getMyOpenShifts || []).filter(canApplyForOpenShift).length;
	const timesheetCount = Number(timesheetData?.getMyTimesheetsPage?.count) || 0;
	const upcomingEntryCount = (unavailabilityData?.getMyUnavailabilities || [])
		.filter(entry => isUpcomingOrPendingUnavailability(entry, today)).length;

	const openPage = useCallback((screen, params) => () => navigation.navigate(screen, params), [navigation]);
	const retryCalendarOpenShifts = useCallback(() => {
		if (calendarOpenShiftRange) {
			refetchCalendarOpenShifts().catch(error => console.error('Calendar open shifts retry failed', error));
		}
	}, [calendarOpenShiftRange, refetchCalendarOpenShifts]);
	const retryRoster = useCallback(() => {
		refetchShifts().catch(error => console.error('Roster shifts retry failed', error));
		retryCalendarOpenShifts();
	}, [refetchShifts, retryCalendarOpenShifts]);
	const changeMonth = useCallback((nextMonth) => {
		setMonth(nextMonth);
		// Keep a day of the shown month selected: today in the current month, otherwise the 1st.
		setSelectedDate(nextMonth === getMonthStart(today) ? today : nextMonth);
	}, [today]);

	const backLabel = previousRouteName === 'Home' ? 'Home' : undefined;

	if (!isAVC) {
		return (
			<Screen backLabel={backLabel} heading="My roster">
				<Card>
					<EmptyState
						icon="calendar"
						title="Not available"
						message="My roster is currently unavailable for this account."
					/>
				</Card>
			</Screen>
		);
	}

	const isShiftsLoading = shiftsLoading && !shiftsData;
	// A month that failed to load (e.g. offline with nothing cached) keeps the
	// calendar so the driver can switch back; the error shows in place of its day.
	const hasShiftsError = Boolean(shiftsError && !shiftsData);
	// Without this, a failed open-shift load would look like a month with no open shifts.
	const hasCalendarOpenShiftsError = Boolean(calendarOpenShiftRange && calendarOpenShiftsError && !calendarOpenShiftsData);
	const hasOpenShiftsError = Boolean(openShiftsError && !openShiftsData);
	const selectedDay = formatRosterDate(selectedDate, 'ddd');
	const selectedDayNumber = formatRosterDate(selectedDate, 'D');

	return (
		<Screen
			backLabel={backLabel}
			heading="My roster"
		>
			<Card style={styles.calendarCard}>
				<MonthCalendar
					month={month}
					onChangeMonth={changeMonth}
					selectedDate={selectedDate}
					onSelectDate={setSelectedDate}
					today={today}
					markedDates={markedDates}
					showLegend={true}
				/>
			</Card>

			<View style={styles.dayList}>
				{hasShiftsError ? (
					<Card tone="warning" style={styles.errorCard}>
						<Text style={styles.errorText}>Unable to load your shifts for this month.</Text>
						<SecondaryButton label="Try again" onPress={retryRoster} />
					</Card>
				) : null}
				{hasCalendarOpenShiftsError && !hasShiftsError ? (
					<Card tone="warning" style={styles.errorCard}>
						<Text style={styles.errorText}>Unable to load open shifts for this month.</Text>
						<SecondaryButton label="Try again" onPress={retryCalendarOpenShifts} />
					</Card>
				) : null}
				{isShiftsLoading ? (
					<Card>
						<Text style={styles.mutedText}>Loading shifts…</Text>
					</Card>
				) : null}
				{!isShiftsLoading && !hasShiftsError && !selectedShifts.length ? (
					<Card style={styles.dayCard}>
						<View style={styles.dayBadge}>
							<Text style={styles.dayName}>{selectedDay}</Text>
							<Text style={styles.dayNumber}>{selectedDayNumber}</Text>
						</View>
						<View style={styles.dayBody}>
							<Text style={styles.dayTitle}>No shift</Text>
							<Text style={styles.mutedText}>No published shift on this day.</Text>
						</View>
					</Card>
				) : null}
				{selectedShifts.map(shift => (
					<Card key={shift.id} tone="current" style={styles.dayCard}>
						<View style={styles.dayBadge}>
							<Text style={styles.dayNameCurrent}>{selectedDay}</Text>
							<Text style={styles.dayNumber}>{selectedDayNumber}</Text>
						</View>
						<View style={styles.dayBody}>
							<Text style={styles.dayTitle}>{shift.areaLabel || 'Shift'}</Text>
							<Text style={styles.currentText}>
								{[
									formatShiftRange(shift.startMinutes, shift.endMinutes),
									formatDurationHours(getShiftDurationHours(shift.startMinutes, shift.endMinutes)),
								].filter(Boolean).join(' · ')}
							</Text>
							{shift.notes ? <Text style={styles.currentText}>{shift.notes}</Text> : null}
						</View>
					</Card>
				))}
				{selectedOpenShifts.map(openShift => (
					<ListRow
						key={openShift.id}
						overline="Open shift"
						title={formatShiftSummary(openShift)}
						subtitle={openShift.notes || 'See Open shifts to apply'}
						onPress={openPage('MyOpenShiftsView', { date: selectedDate })}
					/>
				))}
			</View>

			<View style={styles.links}>
				<ListRow
					title="Open shifts"
					subtitle={hasOpenShiftsError
						? 'Unable to load open shifts'
						: (availableOpenShiftCount
							? `${availableOpenShiftCount} available to apply for`
							: 'None available right now')}
					onPress={openPage('MyOpenShiftsView')}
				/>
				<ListRow
					title="My timesheets"
					subtitle={timesheetCount ? pluralize(timesheetCount, 'timesheet', 'timesheets') : 'No timesheets yet'}
					onPress={openPage('MyTimesheetsView')}
				/>
				<ListRow
					title="Leave"
					subtitle={upcomingEntryCount
						? pluralize(upcomingEntryCount, 'upcoming request', 'upcoming requests')
						: 'No upcoming requests'}
					onPress={openPage('MyLeaveView')}
				/>
			</View>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	calendarCard: {
		padding: 14,
	},
	dayList: {
		gap: space.sm,
	},
	dayCard: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 14,
		paddingVertical: 14,
	},
	dayBadge: {
		width: 44,
		alignItems: 'center',
	},
	dayName: {
		...type.label,
		textTransform: 'uppercase',
		color: colors.textSecondary,
	},
	dayNameCurrent: {
		...type.label,
		textTransform: 'uppercase',
		color: colors.accent,
	},
	dayNumber: {
		...type.title,
		color: colors.text,
	},
	dayBody: {
		flex: 1,
		gap: 2,
	},
	dayTitle: {
		...type.sectionTitle,
		color: colors.text,
	},
	currentText: {
		...type.secondary,
		color: colors.currentSubtle,
	},
	mutedText: {
		...type.secondary,
		color: colors.textSecondary,
	},
	links: {
		gap: space.sm,
	},
	errorCard: {
		gap: space.sm,
	},
	errorText: {
		...type.secondary,
		color: colors.warning,
	},
});

export default MyRosterView;
